import React from "react";
import { Box, Paper, Typography, Button } from "@mui/material";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface BillSummaryCardProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
}

const BillSummaryCard: React.FC<BillSummaryCardProps> = ({ card, onAction }) => {
    if (!card) return null;

    const handleItemAction = (action?: PolicyAction) => {
        if (!action) return;
        onAction?.(action);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #e5e7eb",
                borderRadius: { xs: 1, md: 2 },
                p: { xs: 1, md: 1.5 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
                        {card?.title || "Billing Summary"}
                    </Typography>
                    {card?.subtitle && (
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
                            {card.subtitle}
                        </Typography>
                    )}
                </Box>

                {(card?.sections || []).map((section: any, sIdx: number) => (
                    <Box
                        key={`section_${sIdx}`}
                        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                        {section?.heading && (
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 11, md: 12 },
                                    color: "#374151",
                                }}
                            >
                                {section.heading}
                            </Typography>
                        )}
                        {(section?.items || []).map((item: any, iIdx: number) => (
                            <Paper
                                elevation={0}
                                key={`item_${iIdx}`}
                                sx={{
                                    border: "1px solid #e5e7eb",
                                    borderRadius: { xs: 1, md: 2 },
                                    p: { xs: 1, md: 1.25 },
                                    bgcolor: "#ffffff",
                                    transition:
                                        "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor: "#F5F8FF",
                                        borderColor: "#D1E0FF",
                                        boxShadow: "0 0 0 1px #D1E0FF inset",
                                    },
                                    "&:hover .policy-action-btn": {
                                        bgcolor: "#55B1E6",
                                        color: "#ffffff",
                                        borderRadius: 9999,
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                                        textTransform: "none",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 11, md: 12 },
                                                color: "#374151",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {item?.label}
                                        </Typography>
                                    </Box>
                                    {item?.action && (
                                        <Button
                                            className="policy-action-btn"
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleItemAction(item.action);
                                            }}
                                            sx={{
                                                fontSize: { xs: 10, md: 12 },
                                                px: "6px",
                                                py: "7px",
                                                transition:
                                                    "background-color 150ms ease, color 150ms ease, box-shadow 150ms ease, padding 150ms ease, border-radius 150ms ease",
                                                textTransform: "none",
                                            }}
                                        >
                                            {item.action?.label || "View"}
                                        </Button>
                                    )}
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                ))}

                {(card?.disclaimers || []).length > 0 && (
                    <Typography
                        sx={{ fontSize: { xs: 10, md: 11 }, color: "#6b7280", mt: 1 }}
                    >
                        {card.disclaimers.join(" ")}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

export default BillSummaryCard;
